import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Icons, ImpContainer, ImpIcon, ImpInput, colors, zIndex } from '../..';
import { ImpRotatingChevron } from '../../Atoms/ImpRotatingChevron/ImpRotatingChevron';
import { ANIMATION_DURATION, ANIMATION_DURATION_MS, } from '../../DesignSystem/design-system';
import ImpSelectItem from '../../Molecules/ImpSelectItem/ImpSelectItem';
const fadeIn = keyframes `
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
`;
const fadeOut = keyframes `
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-5px); }
`;
const InputContainer = styled.div `
  & > * {
    position: relative;
    caret-color: transparent;
    ${(props) => props.isDropdownShown === true && `z-index: ${zIndex.selectDropdown};`}
  }
`;
const getOptionsBackground = (showError) => {
    return showError ? colors.surface.surfaceError : colors.surface.surfaceMid;
};
const getOptionsBorder = (showError) => {
    return showError ? colors.border.borderErrorFocus : colors.border.borderFocus;
};
const getColor = (showError, isDisabled) => {
    if (isDisabled) {
        return colors.content.onSurfaceDisabled;
    }
    return showError ? colors.content.onSurfaceError : colors.content.onSurface;
};
const OptionsContainer = styled.div `
  // to cover border bottom of the input field
  margin-top: -1px;
  position: absolute;
  background: ${(props) => getOptionsBackground(props.showError)};
  border: 1px solid ${(props) => getOptionsBorder(props.showError)};
  border-top: none;
  border-radius: 0 0 12px 12px;
  max-height: ${(props) => props.maxHeight};
  overflow: auto;
  z-index: ${zIndex.selectDropdown};
  display: ${(props) => (props.visible ? 'block' : 'none')};
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0px 32px 64px -16px rgba(0, 0, 0, 0.25);

  animation: ${(props) => css `
    ${!props.visible || !props.isAnimating
    ? fadeIn
    : fadeOut} ${ANIMATION_DURATION} linear forwards
  `};

  ::-webkit-scrollbar {
    display: none;
  }
`;
const findOptionToFocus = (options, typedKeys) => {
    const typed = typedKeys.toLowerCase();
    return options.findIndex((option) => {
        const removeSymbolPattern = /[^a-zA-Z0-9\s]/g;
        return option.label
            .toLowerCase()
            .replace(removeSymbolPattern, '')
            .startsWith(typed);
    });
};
/**
 * A select component.
 *
 * @param {OptionData[]} options - The options to be displayed in the dropdown.
 * @param {string} label - The label for the input.
 * @param {string} defaultOption - The default option for the input.
 * @param {string} id - The id for the input field.
 * @param {number} maxLength - The max length for the input field.
 * @param {boolean} showError The flag to show error styling on the dropdown
 * @param {boolean} isDisabled The flag to disable the dropdown
 * @param {string} errorMessage The error message to display
 * @param {string} autocomplete The autocomplete message to apply to selection
 * @param {function} instrumentation - The function to be called when the user selects an option or types a valid option.
 * @param {function} onOptionChange - The function to be called when the user selects an option or types an option.
 */
function ImpSelect({ id = 'select', showError = false, isDisabled = false, errorMessage = '', autocomplete = 'off', defaultOption, options, label, maxLength, maxWidth, instrumentation, onOptionChange, }) {
    const [showOptions, setShowOptions] = useState(false);
    const [focusedOption, setFocusedOption] = useState(-1);
    const [focusedOptionKeyboard, setFocusedOptionKeyboard] = useState(0);
    const [selectedOption, setSelectedOption] = useState(defaultOption !== null && defaultOption !== void 0 ? defaultOption : null);
    const [isUsingKeyboard, setIsUsingKeyboard] = useState(false);
    const [typedKeys, setTypedKeys] = useState('');
    const [optionsMaxHeight, setOptionsMaxHeight] = useState('272px');
    const [isAnimatingDropdown, setIsAnimatingDropdown] = useState(false);
    const [chevronAnimation, setChevronAnimation] = useState('none');
    const inputRef = useRef();
    const optionsRef = useRef(null);
    const timeoutIdRef = useRef(null);
    const wrapperRef = useRef(null);
    const optionRefs = useRef(options.map(() => React.createRef()));
    const displayedErrorMessage = showOptions ? '' : errorMessage;
    useEffect(() => {
        if (defaultOption) {
            setSelectedOption(defaultOption);
        }
    }, [defaultOption]);
    useEffect(() => {
        var _a, _b;
        if (isUsingKeyboard &&
            showOptions &&
            optionRefs.current[focusedOptionKeyboard]) {
            (_a = optionRefs.current[focusedOptionKeyboard].current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                behavior: 'auto',
                block: 'nearest',
            });
        }
        else if (showOptions && optionRefs.current[focusedOption]) {
            (_b = optionRefs.current[focusedOption].current) === null || _b === void 0 ? void 0 : _b.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [focusedOption, focusedOptionKeyboard, showOptions, isUsingKeyboard]);
    useEffect(() => {
        if (showOptions && wrapperRef.current) {
            const distanceToViewportTop = wrapperRef.current.getBoundingClientRect().bottom;
            const viewportHeight = window.innerHeight;
            const maxHeight = viewportHeight - distanceToViewportTop;
            if (maxHeight < 272)
                setOptionsMaxHeight(`${maxHeight}px`);
            if (maxHeight > 272)
                setOptionsMaxHeight('272px');
        }
    }, [showOptions]);
    useEffect(() => {
        return () => {
            if (timeoutIdRef.current !== null) {
                clearTimeout(timeoutIdRef.current);
            }
        };
    }, []);
    const close = () => {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        setIsAnimatingDropdown(true);
        setChevronAnimation('backward');
        setFocusedOptionKeyboard(0);
        setFocusedOption(-1);
        setTimeout(() => {
            setShowOptions(false);
            setIsAnimatingDropdown(false);
        }, ANIMATION_DURATION_MS);
    };
    const handleOptionClick = (option) => {
        setSelectedOption(option);
        onOptionChange(option.value);
        close();
    };
    const handleKeyDown = useCallback((event) => {
        setIsUsingKeyboard(true);
        switch (event.key) {
            case 'Tab':
                close();
                break;
            case 'ArrowDown':
                setFocusedOptionKeyboard((focusedOptionKeyboard + 1) % options.length);
                event.preventDefault();
                break;
            case 'ArrowUp':
                setFocusedOptionKeyboard((focusedOptionKeyboard + options.length - 1) % options.length);
                event.preventDefault();
                break;
            case 'Enter':
                if (!showOptions) {
                    setShowOptions(true);
                }
                else {
                    handleOptionClick(options[focusedOptionKeyboard]);
                }
                break;
            case 'Escape':
                close();
                break;
            default:
                event.preventDefault();
                if (!/^[a-zA-Z0-9]$/.test(event.key)) {
                    return;
                }
                if (timeoutIdRef.current !== null) {
                    clearTimeout(timeoutIdRef.current);
                }
                const newTypedKeys = typedKeys + event.key;
                setTypedKeys(newTypedKeys);
                timeoutIdRef.current = setTimeout(() => {
                    setTypedKeys('');
                }, 1000);
                const optionToFocus = findOptionToFocus(options, newTypedKeys);
                if (optionToFocus !== -1) {
                    setFocusedOptionKeyboard(optionToFocus);
                }
        }
    }, [options, focusedOptionKeyboard, showOptions, selectedOption, typedKeys]);
    const handleOnBlur = () => {
        close();
    };
    const handleOnFocus = () => {
        if (!isDisabled) {
            setShowOptions(true);
        }
    };
    const handleOnClick = (e) => {
        if (isDisabled)
            return;
        e.preventDefault();
        if (!showOptions) {
            inputRef.current.focus();
            setShowOptions(true);
            setChevronAnimation('forward');
        }
        else if (showOptions) {
            close();
        }
    };
    const handleScroll = () => {
        setIsUsingKeyboard(false);
    };
    return (React.createElement(ImpContainer, { dataTestId: "select-container", containerRef: wrapperRef, position: "relative", cursor: "pointer" },
        React.createElement(InputContainer, { isDropdownShown: showOptions, onMouseDown: handleOnClick, "data-testid": "select-input-container" },
            React.createElement(ImpInput, { id: id, maxLength: maxLength, inputRef: inputRef, label: label || 'Default label', value: (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label) || '', showError: showError, isDisabled: isDisabled, errorMessage: displayedErrorMessage, trailingIcon: React.createElement(ImpRotatingChevron, { chevronAnimation: chevronAnimation, onMouseDown: (e) => {
                        // prevents onBlur from closing before the click event takes place
                        e.stopPropagation();
                        e.preventDefault();
                        handleOnClick(e);
                    } },
                    React.createElement(ImpIcon, { size: "24px", stroke: getColor(showError, isDisabled), iconSrc: Icons.chevronDown })), onKeyDown: handleKeyDown, isDropdownShown: showOptions, onBlur: handleOnBlur, onFocus: handleOnFocus, autocomplete: autocomplete, instrumentation: instrumentation, onChange: (e) => {
                    // handles autofill event
                    const filledOption = options.find((option) => option.label === e.target.value);
                    if (filledOption) {
                        setSelectedOption(filledOption);
                        onOptionChange(filledOption.value);
                    }
                }, isSelect: true, maxWidth: maxWidth })),
        (showOptions || isAnimatingDropdown) && (React.createElement(OptionsContainer, { isAnimating: isAnimatingDropdown, visible: showOptions, showError: showError, ref: optionsRef, onMouseDown: (e) => e.preventDefault(), maxHeight: optionsMaxHeight, "data-testid": "select-options-container", onScroll: handleScroll }, options.map((option, index) => {
            return (React.createElement(ImpSelectItem, { key: option.value, option: option, itemRef: optionRefs.current[index], isFocused: index === focusedOption || index === focusedOptionKeyboard, onMouseEnter: () => setFocusedOption(index), handleOptionClick: () => handleOptionClick(option) }));
        })))));
}
export default ImpSelect;
//# sourceMappingURL=ImpSelect.js.map